import { useInfiniteQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';

import { QUERY_KEYS } from '@app/constants/service';
import { formatRelativeTime } from '@app/i18n/formatters/relative-time';
import queryClient from '@app/query-client';

import { buildPaginatedQueryString } from './helpers';
import { GET_GROUPS } from '../constants/endpoints';
import {
  getAccessToken,
  getOktaAccessToken,
  shouldRenewAccessToken,
} from '../helpers/localStorage';
import { IN_TESTING_MODE, TEST_AUTH_TOKEN } from '../tests/constants';
import { Group, GroupsInfinitePage } from '../types/api/group';
import { User } from '../types/api/user';

const buildGroupsQuery = (
  customFilters: {
    searchText?: string;
    ids?: string[];
    limit?: number;
    parentId?: string;
    topLevel?: string;
    includeOktaUserId?: boolean;
  },
  options?: {
    enabled?: boolean;
  },
) => {
  const filters = {
    limit: 32,
    includeActiveStatus: true,
    includeOktaUserId: false,
    ...customFilters,
  };
  async function fetchGroups({ pageParam = 1 }) {
    const filterQueryString = buildPaginatedQueryString(pageParam, filters);
    const url = `${GET_GROUPS}${
      filterQueryString ? `?${filterQueryString}` : ''
    }`;

    let token = TEST_AUTH_TOKEN;
    if (!IN_TESTING_MODE) {
      // this ternary is used here to avoid a promise when possible
      // so that the api calls can be correctly parallelized,
      // please do not abstract the line below to a single promise helper function
      token = shouldRenewAccessToken()
        ? await getAccessToken()
        : getOktaAccessToken();
    }

    return axios({
      url,
      method: 'GET',
      headers: { Authorization: token },
    }).then((response: AxiosResponse) => ({
      groups: response.data.groups.map((group: Group) => ({
        ...group,
        id: group._id,
        members: group.members?.map((member: User) => ({
          ...member,
          id: member._id,
          lastLoginTime: member.lastLoginTime
            ? formatRelativeTime(member.lastLoginTime)
            : null,
          archived: member.active !== undefined ? !member.active : false,
        })),
        leads: group.leads?.map((lead: User) => ({
          ...lead,
          id: lead._id,
          lastLoginTime: lead.lastLoginTime
            ? formatRelativeTime(lead.lastLoginTime)
            : null,
          archived: lead.active !== undefined ? !lead.active : false,
        })),
        subgroups: group.subgroups?.map((subgroup: Group) => ({
          ...subgroup,
          id: subgroup._id,
        })),
      })),
    }));
  }

  return {
    queryKey: [QUERY_KEYS.GROUPS, filters],
    queryFn: fetchGroups,
    getNextPageParam: (
      lastPage: GroupsInfinitePage,
      pages: GroupsInfinitePage[],
    ) => {
      if ((lastPage?.groups?.length || 0) < filters.limit) return undefined;
      return (pages?.length || 0) + 1;
    },
    enabled: options?.enabled,
  };
};

export const useGroupsQuery = (
  customFilters: {
    searchText?: string;
    ids?: string[];
    limit?: number;
    parentId?: string;
    topLevel?: string;
    includeOktaUserId?: boolean;
  },
  options?: {
    enabled?: boolean;
  },
) => useInfiniteQuery(buildGroupsQuery(customFilters, options));

export const prefetchGroupsQuery = (
  customFilters: {
    searchText?: string;
    ids?: string[];
    limit?: number;
    parentId?: string;
    topLevel?: string;
    includeOktaUserId?: boolean;
  },
  options?: {
    enabled?: boolean;
    staleTime?: number;
  },
) =>
  queryClient.prefetchInfiniteQuery(buildGroupsQuery(customFilters, options));
